import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  SummaryTypes,
  useGetSummaryPromptMutation,
  useProcessAudioMutation,
  useProcessTextMutation,
} from "../store/apiSlice";
import { showErrorToast, showWarningToast } from "../toastUtils";
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  ThemeProvider,
  Switch,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
} from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import MicIcon from "@mui/icons-material/Mic";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import SendIcon from "@mui/icons-material/Send";
import InfoIcon from "@mui/icons-material/Info";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import ComputerIcon from "@mui/icons-material/Computer";
import SettingsIcon from "@mui/icons-material/Settings";
import LoadingPage from "../common/LoadingPage";
import { getErrorMessage } from "../error-helpers/errorMessage";
import JsPDF from "jspdf";
import { getWhiteTextLogo } from "../common/commonWidgets";
import { formatElapsedTime } from "../common/commonFuncs";
import logo from "../images/tranzcribe-logo-text-black.png";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import {
  countdownBoxStyles,
  countdownTypographyStyles,
  tabStyles,
} from "./styles";
import { logAnalyticsEvent } from "../config/firebase";
import { logPosthogEvent } from "../common/posthogUtils";
import { darkTheme } from "../theme";
import PromptTab from "./prompt-tab/PromptTab";
import { isMobile } from "react-device-detect";
import DocumentIcon from "@mui/icons-material/Description";
import TextMessageIcon from "@mui/icons-material/Chat";
import EmailIcon from "@mui/icons-material/Email";
import SummarizationDisplayTab from "./SummarizationDisplayTab";
import { useNavigate } from "react-router-dom";
import { useCreateSubscriptionMutation, useGetUserAttributesQuery } from "../store/userApi";
import { SubscriptionLimitModal } from "./subscription/SubscriptionLimitModal";
import { useAuth } from "@clerk/clerk-react";


const TRANSCRIPTION_LIMIT = parseInt(process.env.REACT_APP_FREE_TRANSCRIPTION_LIMIT || '5', 10);

// Browser detection utility
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  
  let browser = 'unknown';
  let version = '';

  if (userAgent.includes('Chrome') && !userAgent.includes('Edge') && !userAgent.includes('OPR') && !isIOS) {
    browser = 'chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    version = match ? match[1] : '';
  } else if (userAgent.includes('CriOS') && isIOS) {
    // Chrome on iOS
    browser = 'chrome';
    const match = userAgent.match(/CriOS\/(\d+)/);
    version = match ? match[1] : '';
  } else if (userAgent.includes('Firefox') && !isIOS) {
    browser = 'firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    version = match ? match[1] : '';
  } else if (userAgent.includes('FxiOS') && isIOS) {
    // Firefox on iOS
    browser = 'firefox';
    const match = userAgent.match(/FxiOS\/(\d+)/);
    version = match ? match[1] : '';
  } else if ((userAgent.includes('Safari') && !userAgent.includes('Chrome')) || isIOS) {
    browser = 'safari';
    const match = userAgent.match(/Version\/(\d+)/);
    version = match ? match[1] : '';
  } else if (userAgent.includes('Edg')) {
    browser = 'edge';
    const match = userAgent.match(/Edg\/(\d+)/);
    version = match ? match[1] : '';
  } else if (userAgent.includes('OPR')) {
    browser = 'opera';
    const match = userAgent.match(/OPR\/(\d+)/);
    version = match ? match[1] : '';
  }

  return { browser, version, isMobile, isIOS };
};

// Generate browser-specific microphone permission instructions
const getMicrophoneInstructions = () => {
  const { browser, isMobile, isIOS } = getBrowserInfo();
  
  const instructions = {
    chrome: {
      desktop: [
        "Click the camera/microphone icon in the address bar (next to the URL)",
        "Select 'Allow' for microphone access",
        "If you don't see the icon, click the lock icon → Site settings → Microphone → Allow",
        "Refresh the page and try recording again"
      ],
      mobile: isIOS ? [
        "Tap 'Allow' when prompted for microphone access",
        "If blocked, you may need to enable microphone in iOS Settings → Privacy & Security → Microphone → Chrome",
        "Chrome on iOS uses Safari's web engine, so also check Safari settings",
        "Refresh the page and try again"
      ] : [
        "Tap the address bar and look for the microphone icon",
        "Tap 'Allow' when prompted for microphone access",
        "If blocked, go to Chrome Settings → Site Settings → Microphone → Allow for this site",
        "Refresh the page and try again"
      ]
    },
    firefox: {
      desktop: [
        "Click the microphone icon in the address bar",
        "Select 'Allow' when prompted",
        "If blocked, click the shield icon → Permissions → Microphone → Allow",
        "You can also go to Settings → Privacy & Security → Permissions → Microphone → Settings",
        "Refresh the page and try recording again"
      ],
      mobile: [
        "Tap 'Allow' when prompted for microphone access",
        "If blocked, tap the menu (three dots) → Settings → Site permissions → Microphone",
        "Find this site and change to 'Allow'",
        "Refresh the page and try again"
      ]
    },
    safari: {
      desktop: [
        "Click 'Allow' when prompted for microphone access",
        "If blocked, go to Safari → Settings → Websites → Microphone",
        "Find this website and change to 'Allow'",
        "Refresh the page and try recording again"
      ],
      mobile: isIOS ? [
        "Tap 'Allow' when prompted for microphone access",
        "If blocked, go to iPhone/iPad Settings → Safari → Camera & Microphone",
        "Make sure 'Ask' or 'Allow' is selected",
        "Also check Settings → Privacy & Security → Microphone → Safari",
        "Return to Safari and refresh the page"
      ] : [
        "Tap 'Allow' when prompted for microphone access",
        "If blocked, check your device's browser settings for microphone permissions",
        "Look for 'Site permissions' or 'Privacy settings' in your browser menu",
        "Return to the page and refresh"
      ]
    },
    edge: {
      desktop: [
        "Click the microphone icon in the address bar",
        "Select 'Allow' when prompted",
        "If blocked, click the lock icon → Permissions → Microphone → Allow",
        "Refresh the page and try recording again"
      ],
      mobile: [
        "Tap 'Allow' when prompted for microphone access",
        "If blocked, tap the menu (three dots) → Settings → Site permissions → Microphone",
        "Change to 'Allow' for this site",
        "Refresh the page and try again"
      ]
    },
    opera: {
      desktop: [
        "Click the microphone icon in the address bar",
        "Select 'Allow' when prompted",
        "If blocked, click the lock icon → Site settings → Microphone → Allow",
        "Refresh the page and try recording again"
      ],
      mobile: [
        "Tap 'Allow' when prompted for microphone access",
        "If blocked, tap the menu → Settings → Site settings → Microphone",
        "Change to 'Allow' for this site",
        "Refresh the page and try again"
      ]
    },
    unknown: {
      desktop: [
        "Look for a microphone or camera icon in your browser's address bar",
        "Click 'Allow' when prompted for microphone access",
        "Check your browser's privacy/security settings for microphone permissions",
        "Refresh the page and try recording again"
      ],
      mobile: [
        "Tap 'Allow' when prompted for microphone access",
        "Check your browser's settings for site permissions",
        "Make sure microphone access is enabled for this site",
        "Refresh the page and try again"
      ]
    }
  };

  const deviceType = isMobile ? 'mobile' : 'desktop';
  return instructions[browser as keyof typeof instructions]?.[deviceType] || instructions.unknown[deviceType];
};

const AudioTranscriber: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [, setAudioUrl] = useState<string | null>(null);
  const [transcribedText, setTranscribedText] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(-1);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [language] = useState<string>("en");
  const [showCompletedIcon, setShowCompletedIcon] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showMicPermissionModal, setShowMicPermissionModal] = useState(false);
  const elapsedTimeRef = useRef(0);
  const [summarizedText, setSummarizedText] = useState<string | null>(null);
  const [isTextMode, setIsTextMode] = useState(false);
  const [directTextInput, setDirectTextInput] = useState('');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioBlobRef = useRef<Blob | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();

  const [getSummaryPrompt, { isLoading: loadingSummary }] =
    useGetSummaryPromptMutation();
  const [processAudio, { isLoading: isLoadingAudio }] = useProcessAudioMutation();
  const [processText, { isLoading: isLoadingText }] = useProcessTextMutation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [createSubscription, { isLoading: isSubscribing }] = useCreateSubscriptionMutation();
  const { data: userData, refetch: refetchUserData } = useGetUserAttributesQuery(undefined, {
    // Skip the query if Clerk auth is not loaded or user is not signed in
    skip: !isLoaded || !isSignedIn || !(window as any).__clerk_jwt,
    // Add retry options to handle transient failures
    refetchOnMountOrArgChange: true,
    pollingInterval: undefined, // Remove polling to avoid unnecessary requests
  });

  const user = useSelector((state: RootState) => state.user.user);

  // Check if authentication is fully ready
  const isAuthReady = isLoaded && isSignedIn && !!(window as any).__clerk_jwt && (userData || user);

  // Function to determine if the user is Pro
  const isUserPro = userData?.hasActiveSubscription || user?.hasActiveSubscription;

  // Add this effect to refetch user data when the user changes
  useEffect(() => {
    // If we have a user ID or email (from clerk or store), refetch to ensure fresh data
    if (user?.id || user?.email) {
      // Reset component state when user changes
      setHasRecorded(false);
      setTranscribedText(null);
      setActiveTab(-1);
      setSummarizedText(null);
      setShowSubscriptionModal(false);

      // Refetch user data to get fresh transcription count
      refetchUserData();
    }
  }, [user?.id, user?.email, refetchUserData]);

  
  const hasReachedTranscriptionLimit = useCallback(() => {
    
    if (isUserPro || !userData) return false;

    
    return userData.transcriptionCount >= TRANSCRIPTION_LIMIT;
  }, [userData, isUserPro]);

  const handleProcessAudio = useCallback(async () => {
    if (!audioBlobRef.current) {
      showWarningToast("No audio recorded. Please record audio first.");
      return;
    }
    
    const formData = new FormData();
    formData.append("audio", audioBlobRef.current, "recording");
    formData.append("language", language);
    formData.append("videoLength", elapsedTimeRef.current.toString());
    try {
      const response = await processAudio(formData).unwrap();
      setTranscribedText(response.transcribedText);
      const summaryText = await getSummaryPrompt({
        text: response.transcribedText,
        type: SummaryTypes.SUMMARY,
      }).unwrap();
      setSummarizedText(summaryText.gptTextResponse);

      // Refresh user data first
      refetchUserData();

      // Check subscription limit after refresh
      const updatedUser = await refetchUserData().unwrap();
      if (updatedUser.transcriptionCount >= TRANSCRIPTION_LIMIT && !updatedUser.hasActiveSubscription) {
        setShowSubscriptionModal(true);
      }

    } catch (error: any) {
      // Check if it's an authentication error
      if (error?.status === 401 || error?.data?.status === 401) {
        console.error('[AudioTranscriber] Authentication error during audio processing');
        showErrorToast("Authentication error. Please refresh the page and try again.");
      } else {
        showErrorToast(`Failed to process audio: ${getErrorMessage(error)}`);
      }
    }
  }, [processAudio, language, getSummaryPrompt, refetchUserData]);

  const startRecording = useCallback(async () => {
    
    if (hasReachedTranscriptionLimit()) {
      setShowSubscriptionModal(true);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      let mimeType: string = "";
      if (MediaRecorder.isTypeSupported("audio/webm")) {
        mimeType = "audio/webm";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      } else if (MediaRecorder.isTypeSupported("audio/mpeg")) {
        mimeType = "audio/mpeg";
      } else {
        showErrorToast("No supported audio format found for recording.");
        return;
      }
      logAnalyticsEvent("startRecording", {
        mimeType,
      });
      logPosthogEvent(
        "startRecording",
        {
          mimeType,
        },
        user?.email ?? 'anonymous-user',
      );

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioBlobRef.current = audioBlob;

        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(audioUrl);
        audioChunksRef.current = [];

        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((track) => track.stop());
          mediaStreamRef.current = null;
        }

        logAnalyticsEvent("stoppedRecording", {
          mimeType,
        });
        logPosthogEvent(
          "stoppedRecording",
          {
            mimeType,
          },
          user?.email ?? 'anonymous-user',
        );

        clearRecordingTimer();
        handleProcessAudio();
        setShowCompletedIcon(true);
        setTimeout(() => setShowCompletedIcon(false), 2000);
        setHasRecorded(true);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setElapsedTime(0);
      elapsedTimeRef.current = 0;
      startRecordingTimer();
    } catch (error) {
      // Check if it's a permission error
      const errorMessage = getErrorMessage(error);
      if (errorMessage.includes('Permission denied') || 
          errorMessage.includes('NotAllowedError') || 
          errorMessage.includes('permission') ||
          (error as any).name === 'NotAllowedError') {
        setShowMicPermissionModal(true);
      } else {
        showErrorToast(
          `Could not access the microphone: ${errorMessage}`,
        );
      }
    }
  }, [hasReachedTranscriptionLimit, user?.email, handleProcessAudio]);

  
  const handleGoToSubscription = useCallback(() => {
    setShowSubscriptionModal(false);
    navigate('/subscription/pro');

    
    logAnalyticsEvent("clickedUpgradeFromLimitModal", {});
    logPosthogEvent(
      "clickedUpgradeFromLimitModal",
      {},
      user?.email ?? 'anonymous-user',
    );
  }, [navigate, user?.email]);

  const startCountdown = () => {
    setCountdown(3);
  };

  useEffect(() => {
    if (countdown === null) return;

    if (countdown > 0) {
      const timer = setTimeout(
        () => setCountdown((prev) => (prev ?? 0) - 1),
        1000,
      );
      return () => clearTimeout(timer);
    }

    if (countdown === 0) {
      startRecording();
      setCountdown(null);
    }
  }, [countdown, startRecording]);

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      clearRecordingTimer();
      setIsRecording(false);
    }
    
    // Ensure media stream is stopped even if mediaRecorder is not active
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }
  };

  const startRecordingTimer = () => {
    recordingTimerRef.current = setInterval(() => {
      elapsedTimeRef.current += 1;
      setElapsedTime(elapsedTimeRef.current); // This updates UI without affecting `elapsedTimeRef`
    }, 1000);
  };

  const clearRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const handleDownload = async (text: string | null, filename: string) => {
    if (!text) return;

    const doc = new JsPDF();
    doc.setFontSize(12);

    // Add the logo at the top (centered)
    const imgWidth = 75; // Adjust width as needed
    const imgHeight = 35; // Adjust height as needed
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = (pageWidth - imgWidth) / 2; // Center the image horizontally

    doc.addImage(logo, "PNG", marginX, 10, imgWidth, imgHeight);

    // Add the text below the logo
    const lines = doc.splitTextToSize(text, 180);
    doc.text(lines, 10, imgHeight + 20);

    // Add black outline around the PDF page
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setLineWidth(0.2);
    doc.setDrawColor(0, 0, 0); // Black color for the outline
    doc.rect(5, 5, pageWidth - 10, pageHeight - 10); // Rectangle outline

    logAnalyticsEvent("handleDownload", {
      filename,
    });
    doc.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    logAnalyticsEvent("handlePromptChange", {
      activeTab: newValue,
    });
    setActiveTab(newValue);
    setSummarizedText(null);
  };

  const handleMicClick = () => {
    if (!isAuthReady) return;
    
    if (hasRecorded) {
      setHasRecorded(false);
      setTranscribedText(null);
      setActiveTab(-1);
      startCountdown();
    } else {
      startCountdown();
    }
  };

  const handleNewTextClick = () => {
    setHasRecorded(false);
    setTranscribedText(null);
    setActiveTab(-1);
    setSummarizedText(null);
    setDirectTextInput('');
  };

  // Handle subscription success
  const handleSubscriptionSuccess = () => {
    // Refresh user data or update state as needed
    // Then allow user to continue recording
    setShowSubscriptionModal(false);
  };

  useEffect(() => {
    // Verificar si el usuario ha alcanzado el límite al cargar el componente
    if (userData && !userData.hasActiveSubscription) {
      const transcriptionCount = userData.transcriptionCount || 0;
      if (transcriptionCount >= TRANSCRIPTION_LIMIT) {
        setShowSubscriptionModal(true);
      }
    }
  }, [userData]);

  // Cleanup effect to stop microphone stream when component unmounts
  useEffect(() => {
    return () => {
      // Stop media recorder if it's active
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      
      // Stop all media stream tracks
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        mediaStreamRef.current = null;
      }
      
      // Clear any active timers
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    };
  }, []);

  const handleModeToggle = () => {
    setIsTextMode(!isTextMode);
    setHasRecorded(false);
    setTranscribedText(null);
    setActiveTab(-1);
    setSummarizedText(null);
    setDirectTextInput('');
  };

  const handleTextSubmit = async () => {
    if (!directTextInput.trim()) {
      showWarningToast("Please enter some text before submitting.");
      return;
    }

    // Check transcription limit before processing
    if (hasReachedTranscriptionLimit()) {
      setShowSubscriptionModal(true);
      return;
    }

    try {
      const response = await processText({ text: directTextInput }).unwrap();
      setHasRecorded(true);
      setTranscribedText(response.transcribedText);
      const summaryText = await getSummaryPrompt({
        text: response.transcribedText,
        type: SummaryTypes.SUMMARY,
      }).unwrap();
      setSummarizedText(summaryText.gptTextResponse);
      refetchUserData();
      setShowCompletedIcon(true);
      setTimeout(() => setShowCompletedIcon(false), 2000);

      // Check subscription limit after processing
      const updatedUser = await refetchUserData().unwrap();
      if (updatedUser.transcriptionCount >= TRANSCRIPTION_LIMIT && !updatedUser.hasActiveSubscription) {
        setShowSubscriptionModal(true);
      }
    } catch (error: any) {
      // Check if it's an authentication error
      if (error?.status === 401 || error?.data?.status === 401) {
        console.error('[AudioTranscriber] Authentication error during text processing');
        showErrorToast("Authentication error. Please refresh the page and try again.");
      } else {
        showErrorToast(`Failed to process text: ${getErrorMessage(error)}`);
      }
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          textAlign: "center",
          color: "#333333",
          backgroundColor: darkTheme.palette.background.default,
          width: "100%",
          height: "calc(100vh - 64px)", 
          overflowY: "auto",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 2,
          pb: 4
        }}
      >
        {/* Updated this Box to adjust the layout */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection={isRecording ? "row" : "column"}
          width="100%"

        >
          {getWhiteTextLogo()}

          
          {userData && !isUserPro && !isRecording && userData.hasActiveSubscription === false && (
            <Box
              sx={{
                mt: 5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  maxWidth: 400,
                  width: '100%'
                }}
              >
                <Typography variant="body1" color="gray" mb={1}>
                  Free Plan
                </Typography>

                <Box sx={{ position: 'relative', width: '100%', mb: 1 }}>
                  <Box
                    sx={{
                      height: 8,
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 4,
                      width: '100%'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: 8,
                      bgcolor: userData.transcriptionCount >= TRANSCRIPTION_LIMIT - 5 ? '#ef4444' : '#4ade80',
                      borderRadius: 4,
                      width: `${(userData.transcriptionCount / TRANSCRIPTION_LIMIT) * 100}%`,
                      transition: 'width 0.5s ease'
                    }}
                  />
                </Box>

                <Typography
                  variant="h6"
                  color={userData.transcriptionCount >= TRANSCRIPTION_LIMIT - 5 ? '#ef4444' : '#ffffff'}
                  fontWeight="bold"
                >
                  {TRANSCRIPTION_LIMIT - userData.transcriptionCount} tranzcribes remaining
                </Typography>
              </Box>


            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '4px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          }}>
            {/* Audio Option */}
            <Box
              onClick={() => setIsTextMode(false)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 3,
                py: 1.5,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backgroundColor: !isTextMode ? '#5D5FEF' : 'transparent',
                color: !isTextMode ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                transform: !isTextMode ? 'scale(1.02)' : 'scale(1)',
                boxShadow: !isTextMode ? '0 4px 12px rgba(93, 95, 239, 0.4)' : 'none',
                '&:hover': {
                  backgroundColor: !isTextMode ? '#4A4BD5' : 'rgba(255, 255, 255, 0.1)',
                  color: !isTextMode ? '#ffffff' : 'rgba(255, 255, 255, 0.9)',
                  transform: 'scale(1.02)',
                },
                marginRight: 0.6
              }}
            >
              <MicIcon sx={{ 
                mr: 1, 
                fontSize: '1.2rem',
                filter: !isTextMode ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
              }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: !isTextMode ? 600 : 500,
                  fontSize: '0.875rem',
                  letterSpacing: '0.025em'
                }}
              >
                Audio
              </Typography>
            </Box>

            {/* Text Option */}
            <Box
              onClick={() => setIsTextMode(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 3,
                py: 1.5,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backgroundColor: isTextMode ? '#5D5FEF' : 'transparent',
                color: isTextMode ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                transform: isTextMode ? 'scale(1.02)' : 'scale(1)',
                boxShadow: isTextMode ? '0 4px 12px rgba(93, 95, 239, 0.4)' : 'none',
                '&:hover': {
                  backgroundColor: isTextMode ? '#4A4BD5' : 'rgba(255, 255, 255, 0.1)',
                  color: isTextMode ? '#ffffff' : 'rgba(255, 255, 255, 0.9)',
                  transform: 'scale(1.02)',
                }
              }}
            >
              <TextFieldsIcon sx={{ 
                mr: 1, 
                fontSize: '1.2rem',
                filter: isTextMode ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
              }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: isTextMode ? 600 : 500,
                  fontSize: '0.875rem',
                  letterSpacing: '0.025em'
                }}
              >
                Text
              </Typography>
            </Box>
          </Box>
        </Box>

        {isTextMode && !transcribedText && (
          <Box sx={{ width: '80%', maxWidth: '800px', mt: 4, mb: 4, mx: 'auto' }}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, backgroundColor: '#2a2a2a' }}>
              <Typography variant="h6" gutterBottom color="white">
                Enter your text
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={12}
                variant="outlined"
                value={directTextInput}
                onChange={(e) => setDirectTextInput(e.target.value)}
                placeholder={isAuthReady ? "Type or paste your text here..." : "Initializing..."}
                disabled={!isAuthReady}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-disabled': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: 'white',
                    '&.Mui-disabled': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      WebkitTextFillColor: 'rgba(255, 255, 255, 0.5)',
                    },
                  },
                }}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && isAuthReady) {
                    e.preventDefault();
                    handleTextSubmit();
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Press Ctrl+Enter to submit
                </Typography>
                              <Button
                variant="contained"
                onClick={handleTextSubmit}
                disabled={!isAuthReady}
                startIcon={!isAuthReady ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(25, 118, 210, 0.5)',
                    color: 'rgba(255, 255, 255, 0.7)',
                  }
                }}
              >
                {isAuthReady ? 'Submit Text' : 'Initializing...'}
              </Button>
              </Box>
            </Paper>
          </Box>
        )}

        {isTextMode && hasRecorded && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 4 }}>
            <Button 
              color="primary" 
              onClick={handleNewTextClick} 
              sx={{
                ...tabStyles,
                // Mobile-specific overrides
                px: { xs: 3, sm: 2 }, // More padding on mobile
                py: { xs: 1.5, sm: 1 },
                minWidth: { xs: '160px', sm: 'auto' }, // Ensure minimum width on mobile
                maxWidth: { xs: '220px', sm: 'none' }, // Prevent overflow on mobile
              }}
            >
              <Box 
                color={"#ffffff"}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <TextFieldsIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                <Typography 
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }, // Smaller text on mobile
                    fontWeight: 500,
                    textAlign: 'center',
                    lineHeight: 1.2,
                    whiteSpace: { xs: 'normal', sm: 'nowrap' }, // Allow text wrap on mobile
                  }}
                >
                  Enter new text
                </Typography>
              </Box>
            </Button>
          </Box>
        )}

        {!isTextMode && (
          <>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 0, flexDirection: "column", alignItems: "center" }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={handleMicClick}
                sx={{ textAlign: "center", mb: 4, cursor: "pointer", mt: 4 }}
              >
                {hasRecorded && (
                  <Button 
                    color="primary" 
                    onClick={handleMicClick}
                    disabled={!isAuthReady}
                    sx={{
                      ...tabStyles,
                      // Mobile-specific overrides
                      px: { xs: 3, sm: 2 }, // More padding on mobile
                      py: { xs: 1.5, sm: 1 },
                      minWidth: { xs: '200px', sm: 'auto' }, // Ensure minimum width on mobile
                      maxWidth: { xs: '280px', sm: 'none' }, // Prevent overflow on mobile
                    }}
                  >
                    <Box 
                      color={"#ffffff"}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      {isAuthReady ? (
                        <MicIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      ) : (
                        <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                      )}
                      <Typography 
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }, // Smaller text on mobile
                          fontWeight: 500,
                          textAlign: 'center',
                          lineHeight: 1.2,
                          whiteSpace: { xs: 'normal', sm: 'nowrap' }, // Allow text wrap on mobile
                        }}
                      >
                        {isAuthReady ? 'Record a new message' : 'Initializing...'}
                      </Typography>
                    </Box>
                  </Button>
                )}
              </Box>

              {!hasRecorded && countdown === null && (
                <Box mt={10} mb={8}>
                  {!isAuthReady ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress size={60} sx={{ color: '#ffffff' }} />
                      <Typography variant="body1" sx={{ mt: 2, color: '#ffffff' }}>
                        Initializing...
                      </Typography>
                    </Box>
                  ) : (
                    <IconButton
                      color={isRecording ? "secondary" : "primary"}
                      onClick={isRecording ? stopRecording : startCountdown}
                      disabled={!isAuthReady}
                      sx={{
                        mb: 2,
                        color: "#FFFFFF",
                        transform: "scale(5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        margin: "0 auto",
                        width: "fit-content",
                        animation: isRecording ? "pulsate 2s infinite" : "none",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        boxShadow: "0px 0px 5px rgba(255, 255, 255, 0.5)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                        "@keyframes pulsate": {
                          "0%": {
                            transform: "scale(5)",
                            color: "#FFFFFF",
                          },
                          "50%": {
                            transform: "scale(5.1)",
                            color: "red",
                          },
                          "100%": {
                            transform: "scale(5)",
                            color: "#FFFFFF",
                          },
                        },
                      }}
                    >
                      {isRecording ? (
                        <StopIcon fontSize="large" />
                      ) : (
                        <MicIcon
                          fontSize="large"
                          sx={{
                            color: "#FFFFFF",
                            padding: "4px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            boxShadow: "0px 0px 5px rgba(255, 255, 255, 0.5)",
                          }}
                        />
                      )}
                    </IconButton>
                  )}
                </Box>
              )}

              {countdown !== null ? (
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  sx={countdownBoxStyles}
                  onClick={() => {
                    setCountdown(null);
                  }}
                >
                  <Typography variant="h1" sx={countdownTypographyStyles}>
                    {countdown}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#fff",
                      fontWeight: "medium",
                      mt: 1,
                    }}
                  >
                    Stop
                  </Typography>
                </Box>
              ) : isRecording ? (
                <Typography
                  variant="h6"
                  sx={{ mt: 10, color: "red", fontSize: "32px" }}
                >
                  {formatElapsedTime(elapsedTime)}
                </Typography>
              ) : showCompletedIcon ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <ThumbUpIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ color: "green" }}>
                    Completed!
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </>
        )}

        {isLoadingAudio || isLoadingText || loadingSummary ? <LoadingPage /> : null}

        {transcribedText && (
          <Box>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              centered
              textColor="inherit"
              scrollButtons={isMobile}
              TabIndicatorProps={{ style: { display: "none" } }}
            >
              {user && user.prompts && user.prompts.map((prompt, index) => {
                // Determine the icon for the tab
                const TabIcon =
                  index === 2
                    ? DocumentIcon
                    : index === 1
                      ? TextMessageIcon
                      : index === 0
                        ? EmailIcon
                        : null;

                return (
                  <Tab
                    key={prompt.id}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {TabIcon && <TabIcon />}
                        {prompt.title}
                      </Box>
                    }
                    sx={tabStyles}
                  />
                );
              })}
            </Tabs>
            {user && user.prompts && user.prompts.length === 0 && (
              <Typography>You do not have any prompts!</Typography>
            )}
            {user && user.prompts && user.prompts.map((prompt, index) =>
              activeTab === index ? (
                <PromptTab
                  key={prompt.id}
                  prompt={prompt}
                  inputText={transcribedText}
                  handleDownload={handleDownload}
                />
              ) : (
                <Box key={prompt.id} />
              ),
            )}
            {summarizedText !== null && (
              <SummarizationDisplayTab text={summarizedText} />
            )}
          </Box>
        )}
      </Box>
      <SubscriptionLimitModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onSuccess={handleSubscriptionSuccess}
      />

      {/* Microphone Permission Instructions Modal */}
      <Dialog
        open={showMicPermissionModal}
        onClose={() => setShowMicPermissionModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: darkTheme.palette.background.paper,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          color: darkTheme.palette.text.primary,
          borderBottom: `1px solid ${darkTheme.palette.divider}`
        }}>
          <MicIcon color="primary" />
          <Box>
            <Typography variant="h6" component="div">
              Microphone Access Required
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {getBrowserInfo().isMobile ? (
                <PhoneAndroidIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              ) : (
                <ComputerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
              <Chip 
                label={`${getBrowserInfo().browser.charAt(0).toUpperCase() + getBrowserInfo().browser.slice(1)} ${getBrowserInfo().version}`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
              We need access to your microphone to record audio for transcription. 
              Please follow these steps to enable microphone permissions:
            </Typography>
          </Box>

          <List sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.05)', 
            borderRadius: 1, 
            p: 2,
            border: `1px solid ${darkTheme.palette.divider}`
          }}>
            {getMicrophoneInstructions().map((instruction, index) => (
              <ListItem key={index} sx={{ py: 1, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {index + 1}
                  </Box>
                </ListItemIcon>
                <ListItemText 
                  primary={instruction}
                  primaryTypographyProps={{
                    color: 'text.primary',
                    fontSize: '0.95rem'
                  }}
                />
              </ListItem>
            ))}
          </List>

          <Box sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: 'rgba(33, 150, 243, 0.1)', 
            borderRadius: 1,
            border: '1px solid rgba(33, 150, 243, 0.3)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <InfoIcon sx={{ color: 'info.main', fontSize: 20 }} />
              <Typography variant="subtitle2" color="info.main" fontWeight="bold">
                Additional Tips
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              • Make sure your microphone is connected and working
              • Try refreshing the page after changing permissions
              • Some browsers require you to interact with the page before requesting microphone access
              • If you're still having issues, try using an incognito/private window
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: `1px solid ${darkTheme.palette.divider}` }}>
          <Button
            onClick={() => setShowMicPermissionModal(false)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Close
          </Button>
          <Button
            onClick={() => {
              setShowMicPermissionModal(false);
              // Retry recording after a short delay
              setTimeout(() => {
                startRecording();
              }, 500);
            }}
            variant="contained"
            startIcon={<MicIcon />}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            }}
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AudioTranscriber;
